import StartIcon from "@mui/icons-material/Start";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetInfo } from "../api/scan-tool.api";
import Html5QrcodePlugin from "../components/qr-scan";
import SuccessOverlay from "../components/success-overlay";
import ErrorDialog from "../components/error-dialog";
import { getActionSelector } from "../components/scan-module-ui/registry";
import { useScanHandler } from "../hooks/useScanHandler";
import { cleanupAudio } from "../utils/audio-feedback";
import lang from "../i18n/lang";
import { ScansubmoduleDto } from "../dtos/participantdata/scanmodule/scansubmodule.dto";
import { ModuleTypeEnum } from "../dtos/participantdata/scanmodule/module-type.enum";
import { AttendanceStatusEnum } from "../dtos/participantdata/scanmodule/attendance-status.enum";

function TicketScan({ token }: { token: string }) {
  const { scanModuleId } = useParams();
  const isWideScreen = useMediaQuery("(min-width: 1000px)");
  const style = {
    marginTop: "3.5em",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: 1,
    bgcolor: "#FFFFFF",
    width: isWideScreen ? "40%" : "100%",
  };

  const { data: scanInfo, isLoading, isError } = useGetInfo(token);

  const [scanResult, setScanResult] = useState("");
  const [scanModule, setScanModule] = useState<ScansubmoduleDto>(
    scanInfo?.scansubmodules.find(
      (scanmodule) => scanmodule.id === scanModuleId,
    ),
  );
  const [cameraKey] = useState(0); // Key for camera instance
  const [action, setAction] = useState<string | null>(null);

  const ActionSelector = scanModule?.moduleType
    ? getActionSelector(scanModule.moduleType)
    : null;

  // Use custom hook for scan handling
  const {
    delegateNumber,
    isScanning,
    scanStatus,
    errorMessage,
    successMessage,
    scanCooldown,
    setDelegateNumber,
    handleScan,
    resetError,
  } = useScanHandler({
    scanModuleId: scanModuleId || "",
    token,
    moduleType: scanModule?.moduleType,
    action,
  });

  useEffect(() => {
    const module = scanInfo?.scansubmodules.find(
      (scanmodule) => scanmodule.id === scanModuleId,
    );
    setScanModule(module);
    setAction(
      module?.moduleType === ModuleTypeEnum.ATTENDANCE
        ? AttendanceStatusEnum.CHECKED_IN
        : null,
    );
    // Cleanup audio on unmount
    return () => {
      cleanupAudio();
    };
  }, [scanInfo]);

  // Auto-scan when QR code is detected
  useEffect(() => {
    if (
      scanResult &&
      !isScanning &&
      scanStatus === "idle" &&
      !scanCooldown &&
      scanModuleId
    ) {
      handleScan(scanResult, action);
      // Reset scanResult to allow new scans
      setScanResult("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResult, isScanning, scanStatus, scanCooldown, scanModuleId]);

  const submitDelegateNumber = async () => {
    if (delegateNumber && !isScanning && scanStatus === "idle") {
      await handleScan(delegateNumber, action);
    }
  };

  const onNewScanResult = (decodedText: string, decodedResult: unknown) => {
    if (decodedText && !isScanning && scanStatus === "idle" && !scanCooldown) {
      setScanResult(decodedText);
    }
  };

  if (isLoading) return <Typography align="center">Loading...</Typography>;
  if (isError)
    return (
      <Typography align="center" color="error">
        Failed to load scan information.
      </Typography>
    );

  return (
    <>
      {/* Success Overlay */}
      <SuccessOverlay
        open={scanStatus === "success"}
        message={successMessage}
      />

      {/* Error Dialog */}
      <ErrorDialog
        open={scanStatus === "error"}
        message={errorMessage}
        onClose={resetError}
      />

      {/* Main Content */}
      <Paper sx={style} elevation={5}>
        <Box p={3}>
          <Stack spacing={3}>
            <Typography
              align="left"
              variant="h4"
              sx={{ fontWeight: "600", color: "#00537D" }}
            >
              {`${scanModule?.name} `}
            </Typography>
            {ActionSelector && (
              <ActionSelector value={action} onChange={setAction} />
            )}
            <Typography
              align="left"
              variant="h5"
              sx={{ fontWeight: "600", color: "#00537D" }}
            >
              {`${scanInfo.eventName} - ${scanInfo.userName}`}
            </Typography>

            {/* Camera Scanner - Always rendered and running, only hidden during error dialog */}
            <Html5QrcodePlugin
              key={cameraKey}
              instanceId={cameraKey}
              fps={10}
              qrbox={250}
              disableFlip={false}
              hidden={scanStatus === "error"}
              qrCodeSuccessCallback={onNewScanResult}
            />

            {/* Manual Entry (still available) */}
            <Box display="flex" width="100%" justifyContent="center">
              <TextField
                value={delegateNumber}
                onChange={(e) => setDelegateNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && delegateNumber) {
                    submitDelegateNumber();
                  }
                }}
                label={lang("scan.delegate.number")}
                disabled={isScanning || scanStatus !== "idle"}
              />
              <IconButton
                onClick={submitDelegateNumber}
                disabled={
                  !delegateNumber || isScanning || scanStatus !== "idle"
                }
                size="large"
                color="primary"
              >
                <StartIcon />
              </IconButton>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </>
  );
}

export default TicketScan;
